import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Card, Icon, breakpoints } from '@soluto-private/mx-asurion-ui-react';

import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import { useGoToNextStep } from '@service-initiation/shared-data-access-flow-manager';
import { useIsDesktop } from '@service-initiation/shared-ui-breakpoints';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { StepContentSkeleton } from '@service-initiation/shared-ui-step-content-skeleton';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  DeliveryMethod,
  completedDeliverySelection,
  deliverySelectionConfirmedAtom,
  deliverySelectionPageValidAsyncAtom,
  isAlternativeEligibility,
  store,
  useDeliveryMethodAsyncAtom,
  useDeliveryMethodAtom,
  useFindClosestStoreForMailIn,
  useGetRemoteTechEligibility,
  useMailInValue,
  useRemoteTechValue,
  useSelectedDeviceTypeAsyncAtom,
  useSelectedRepairsAsyncAtom,
  useSelectedStoreAsyncAtom,
  validateRemoteTech,
} from '@service-initiation/tech-care-data-access-store';
import { type ErrorType } from '@service-initiation/tech-care-data-access-tech-care-services';
import { NavigationButtonsContainer } from '@service-initiation/tech-care-ui-navigation-buttons';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';

import { MailInTab } from './tabs/mail-in-tab';
import { RemoteTechTab } from './tabs/remote-tech-tab';
import { StoreSelectionTab } from './tabs/store-selection-tab';

const Container = styled.div``;

const TABSTEXT = TranslationEntries.Repairs.StoreLocator.Tabs;

const TabContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 24px;
  gap: 8px;
`;

const StyledCard = styled(Card)<{ isMobile: boolean; selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 12px;

  && {
    padding: 1rem 0.55rem;
  }

  ${({ selected }) => (selected ? ' font-weight: bold;' : '')}

  ${breakpoints.md} {
    flex-direction: row;
  }
`;

const ShippingTruckIcon = styled(Icon)`
  ${breakpoints.md} {
    margin-bottom: 4px;
  }
`;

const TitleContainer = styled.div`
  padding-bottom: 20px;
`;

const TITLES: Record<string, string> = {
  [DeliveryMethod.ComeToMe]:
    TranslationEntries.Repairs.StoreLocator.RemoteTech.CallToAction,
  [DeliveryMethod.MailIn]:
    TranslationEntries.Repairs.StoreLocator.MailIn.CallToAction,
  [DeliveryMethod.VisitStore]:
    TranslationEntries.Repairs.StoreLocator.VisitStore.CallToAction,
};

export const DeliverySelectionPage = () => {
  const { value: selectedRepairs = [] } = useSelectedRepairsAsyncAtom();

  const [, setDeliveryMethod] = useDeliveryMethodAtom();

  const { loading: selectedDeviceTypeLoading, value: selectedDeviceType } =
    useSelectedDeviceTypeAsyncAtom();

  const { loading: deliveryMethodLoading, value: deliveryMethod } =
    useDeliveryMethodAsyncAtom();

  const mailIn = useMailInValue();
  const remoteTech = useRemoteTechValue();
  const { dispatchClickEvent } = useAnalytics();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const [, goToNextStep] = useGoToNextStep();

  const [mailInState, miFindClosestStore] = useFindClosestStoreForMailIn();

  const [remoteTechState, getRemoteTechEligibility] =
    useGetRemoteTechEligibility();

  const { value: selectedStore } = useSelectedStoreAsyncAtom();

  const onContinueButtonClick = useCallback(async () => {
    if (deliveryMethod === DeliveryMethod.MailIn) {
      await miFindClosestStore(mailIn);
      store.set(deliverySelectionConfirmedAtom, true);
    } else if (deliveryMethod === DeliveryMethod.ComeToMe) {
      const rtEligibility = await getRemoteTechEligibility();
      if (validateRemoteTech(rtEligibility)) {
        store.set(deliverySelectionConfirmedAtom, true);
      }
    }
    const deliverySelectionPageValid = await store.get(
      deliverySelectionPageValidAsyncAtom
    );

    if (deliverySelectionPageValid) {
      goToNextStep();
    }
  }, [
    deliveryMethod,
    getRemoteTechEligibility,
    goToNextStep,
    mailIn,
    miFindClosestStore,
  ]);

  const onTabSelection = (deliveryMethod: DeliveryMethod) => {
    setDeliveryMethod(deliveryMethod);
    dispatchClickEvent('DeliveryMethodChange', {
      Cta: deliveryMethod,
    });
  };

  const { ref: elementRef } = useInViewAnalytics('DeliverySelectionPage');

  return (
    <PageContainer>
      {!deliveryMethodLoading &&
      !selectedDeviceTypeLoading &&
      deliveryMethod ? (
        <MainContentContainer>
          <LeftMainContentSection>
            <Container data-testid="delivery-selection" ref={elementRef}>
              <TabContainer>
                <>
                  <StyledCard
                    ariaLabel="Visit store tab"
                    data-testid="visit-store-tab"
                    element="button"
                    isMobile={!isDesktop}
                    onClick={() => {
                      onTabSelection(DeliveryMethod.VisitStore);
                    }}
                    selected={deliveryMethod === DeliveryMethod.VisitStore}
                    type="selection"
                  >
                    <Icon size="medium" src="shopping/store" />
                    {t(TABSTEXT.VisitStore)}
                  </StyledCard>
                  <StyledCard
                    ariaLabel="Mail in tab"
                    data-testid="mail-in-tab"
                    element="button"
                    isMobile={!isDesktop}
                    onClick={() => {
                      onTabSelection(DeliveryMethod.MailIn);
                    }}
                    selected={deliveryMethod === DeliveryMethod.MailIn}
                    type="selection"
                  >
                    <Icon size="medium" src="shipping-and-delivery/box-1" />
                    {t(TABSTEXT.MailIn)}
                  </StyledCard>
                  {isAlternativeEligibility(
                    selectedRepairs,
                    selectedDeviceType
                  ) && (
                    <StyledCard
                      ariaLabel="Remote tech tab"
                      data-testid="come-to-me-tab"
                      element="button"
                      isMobile={!isDesktop}
                      onClick={() => {
                        onTabSelection(DeliveryMethod.ComeToMe);
                      }}
                      selected={deliveryMethod === DeliveryMethod.ComeToMe}
                      type="selection"
                    >
                      <ShippingTruckIcon
                        size="medium"
                        src="shipping-and-delivery/shipping-truck-delivery-time"
                      />
                      {t(TABSTEXT.ComeToMe)}
                    </StyledCard>
                  )}
                </>
              </TabContainer>
              <TitleContainer>
                <StepTitle title={t(TITLES[deliveryMethod])} />
              </TitleContainer>
              {deliveryMethod === DeliveryMethod.VisitStore && (
                <StoreSelectionTab />
              )}

              {deliveryMethod === DeliveryMethod.MailIn && (
                <MailInTab
                  validationError={
                    mailInState.error &&
                    (mailInState.error.message as ErrorType)
                  }
                />
              )}

              {deliveryMethod === DeliveryMethod.ComeToMe && (
                <RemoteTechTab
                  validationError={
                    remoteTechState.error &&
                    (remoteTechState.error.message as ErrorType)
                  }
                />
              )}
            </Container>
          </LeftMainContentSection>
          <NavigationButtonsContainer
            disableContinueButton={
              deliveryMethod !== DeliveryMethod.VisitStore
                ? !completedDeliverySelection(
                    deliveryMethod,
                    mailIn,
                    remoteTech,
                    selectedStore
                  )
                : undefined
            }
            isLoading={mailInState.loading || remoteTechState.loading}
            onContinueButtonClick={
              deliveryMethod !== DeliveryMethod.VisitStore
                ? onContinueButtonClick
                : undefined
            }
            pageName="DeliverySelection"
            showBackButton={true}
          />
        </MainContentContainer>
      ) : (
        <StepContentSkeleton />
      )}
    </PageContainer>
  );
};
